import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaChevronDown, FaPlus, FaTrash } from "react-icons/fa";
import "../assets/css/AddLead.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/Issues.css";
import axiosInstance from "../components/api/axiosInstance";
import QuoteGenerator from "../components/QuoteGenerator";

function AddBusinessLead({ onBack, onAddLead }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    personalEmail: "",
    personalPhone: "",
    altPhone: "",
    companyName: "",
    domain: "",
    companyEmail: "",
    companyPhone: "",
    website: "",
    communicationLanguage: "",
    source: "",
    status: "",
    isQuoteSent: false,
    services: [],
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "",
    zipCode: ""
  });

  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [selectedServiceCategoryId, setSelectedServiceCategoryId] = useState(null);
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [addressSearch, setAddressSearch] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingAddressSuggestions, setLoadingAddressSuggestions] = useState(false);
  const addressSearchTimeoutRef = useRef(null);
  const addressSearchRef = useRef(null);
  const [customServices, setCustomServices] = useState({});
  const [editingService, setEditingService] = useState(null);
  const [isAddingServiceToCategory, setIsAddingServiceToCategory] = useState(null);
  const [phoneCountryCode, setPhoneCountryCode] = useState("");
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const domainDropdownRef = useRef(null);
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [sourceSearchQuery, setSourceSearchQuery] = useState("");
  const sourceDropdownRef = useRef(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSearchQuery, setStatusSearchQuery] = useState("");
  const statusDropdownRef = useRef(null);

  // Fetch services/categories from API
  useEffect(() => {
    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        const response = await axiosInstance.get('/service/categories');
        if (response.data && Array.isArray(response.data)) {
          setServiceCategories(response.data);
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, []);

  // Fetch lead statuses for dropdown (same API as filter section)
  useEffect(() => {
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const response = await axiosInstance.get("/lead-statuses");
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        const mapped = raw
          .map((s) => ({
            id: s.Status_Id ?? s.status_id ?? s.id,
            name: s.Status ?? s.status ?? ""
          }))
          .filter((s) => s.id != null && s.name.trim() !== "");
        setLeadStatuses(mapped);
      } catch (error) {
        console.error("Error fetching lead statuses for business lead:", error);
        setLeadStatuses([]);
      } finally {
        setLoadingStatuses(false);
      }
    };

    fetchStatuses();
  }, []);

  // Fetch domains for dropdown (same API as filter section)
  useEffect(() => {
    const fetchDomains = async () => {
      setLoadingDomains(true);
      try {
        const response = await axiosInstance.get("/domains");
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        const mapped = raw
          .map((d) => ({
            id: d.Domain_Id ?? d.domain_id ?? d.id,
            name: d.Domain_Name ?? d.domain_name ?? d.name ?? ""
          }))
          .filter((d) => d.id != null && d.name.trim() !== "");
        setDomains(mapped);
      } catch (error) {
        console.error("Error fetching domains for business lead:", error);
        setDomains([]);
      } finally {
        setLoadingDomains(false);
      }
    };
    fetchDomains();
  }, []);

  // Fetch lead sources from API
  useEffect(() => {
    const fetchSources = async () => {
      setLoadingSources(true);
      try {
        const response = await axiosInstance.get('/lead-sources');
        if (response.data && Array.isArray(response.data)) {
          setSources(response.data);
        }
      } catch (error) {
        console.error("Error fetching sources:", error);
      } finally {
        setLoadingSources(false);
      }
    };

    fetchSources();
  }, []);

  // Fetch countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await axiosInstance.get('/countries');
        if (response.data && Array.isArray(response.data)) {
          setCountries(response.data);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  // Fetch states when country is selected
  useEffect(() => {
    const fetchStates = async () => {
      if (!selectedCountryId) {
        setStates([]);
        setFormData(prev => ({ ...prev, state: "" }));
        return;
      }

      setLoadingStates(true);
      try {
        // Try /states/{country_id} endpoint
        const response = await axiosInstance.get(`/states/${selectedCountryId}`);
        if (response.data && Array.isArray(response.data)) {
          setStates(response.data);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
        // If /states/{id} fails, try alternative endpoints
        try {
          const altResponse = await axiosInstance.get(`/api/states/${selectedCountryId}`);
          if (altResponse.data && Array.isArray(altResponse.data)) {
            setStates(altResponse.data);
          }
        } catch (altError) {
          console.error("Error with alternative endpoint:", altError);
          setStates([]);
        }
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, [selectedCountryId]);

  // Fetch cities when state is selected
  useEffect(() => {
    const fetchCities = async () => {
      if (!selectedStateId) {
        setCities([]);
        setFormData(prev => ({ ...prev, city: "" }));
        return;
      }

      setLoadingCities(true);
      try {
        const response = await axiosInstance.get(`/cities/${selectedStateId}`);
        if (response.data && Array.isArray(response.data)) {
          setCities(response.data);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCities();
  }, [selectedStateId]);

  // Fetch address suggestions from Google Maps API
  const fetchAddressSuggestions = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoadingAddressSuggestions(true);
    try {
      const response = await axiosInstance.get(`/get-address?address=${encodeURIComponent(searchTerm.trim())}`);
      if (response.data && response.data.suggestions && Array.isArray(response.data.suggestions)) {
        setAddressSuggestions(response.data.suggestions);
        setShowSuggestions(true);
      } else {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoadingAddressSuggestions(false);
    }
  };

  // Handle address search input with debouncing
  const handleAddressSearchChange = (e) => {
    const value = e.target.value;
    setAddressSearch(value);

    // Clear previous timeout
    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
    }

    // Set new timeout for debouncing (500ms delay)
    addressSearchTimeoutRef.current = setTimeout(() => {
      fetchAddressSuggestions(value);
    }, 500);
  };

  // Fetch address details when a suggestion is selected
  const handleAddressSuggestionSelect = async (suggestion) => {
    setAddressSearch(suggestion.description);
    setShowSuggestions(false);
    setAddressSuggestions([]);

    try {
      const response = await axiosInstance.get(`/get-address-details?placeId=${encodeURIComponent(suggestion.place_id)}`);
      if (response.data) {
        const details = response.data;
        
        // Update form data with address details
        setFormData(prev => ({
          ...prev,
          addressLine1: details.addressLine1 || prev.addressLine1,
          addressLine2: details.addressLine2 || prev.addressLine2,
          zipCode: details.postalCode || prev.zipCode
        }));

        // Find and set country
        if (details.country) {
          const countryMatch = countries.find(c => 
            c.country_name?.toLowerCase() === details.country.toLowerCase()
          );
          if (countryMatch) {
            setSelectedCountryId(countryMatch.country_id);
            setFormData(prev => ({ ...prev, country: countryMatch.country_name }));
            
            // Fetch states for the selected country
            try {
              const statesResponse = await axiosInstance.get(`/states/${countryMatch.country_id}`);
              if (statesResponse.data && Array.isArray(statesResponse.data)) {
                setStates(statesResponse.data);
                
                    // Find and set state
                    if (details.state) {
                      const stateMatch = statesResponse.data.find(s => 
                        s.state_name?.toLowerCase() === details.state.toLowerCase()
                      );
                      if (stateMatch) {
                        setSelectedStateId(stateMatch.state_id);
                        setFormData(prev => ({ ...prev, state: stateMatch.state_name }));
                        
                        // Fetch cities for the selected state
                        try {
                          const citiesResponse = await axiosInstance.get(`/cities/${stateMatch.state_id}`);
                          if (citiesResponse.data && Array.isArray(citiesResponse.data)) {
                            setCities(citiesResponse.data);
                            
                            // Find and set city
                            if (details.city) {
                              const cityMatch = citiesResponse.data.find(c => 
                                c.city_name?.toLowerCase() === details.city.toLowerCase()
                              );
                              if (cityMatch) {
                                setFormData(prev => ({ ...prev, city: cityMatch.city_name }));
                              } else {
                                // If city not found in list, set it directly
                                setFormData(prev => ({ ...prev, city: details.city }));
                              }
                            }
                          } else {
                            // If cities fetch fails, still set city name
                            if (details.city) {
                              setFormData(prev => ({ ...prev, city: details.city }));
                            }
                          }
                        } catch (cityError) {
                          console.error("Error fetching cities:", cityError);
                          // Still set city name even if fetch fails
                          if (details.city) {
                            setFormData(prev => ({ ...prev, city: details.city }));
                          }
                        }
                      } else {
                        // If state not found, set it directly
                        setFormData(prev => ({ ...prev, state: details.state }));
                      }
                    }
              }
            } catch (stateError) {
              console.error("Error fetching states:", stateError);
              // Still set state name even if fetch fails
              if (details.state) {
                setFormData(prev => ({ ...prev, state: details.state }));
              }
            }
          } else {
            // If country not found, set it directly
            setFormData(prev => ({ ...prev, country: details.country }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching address details:", error);
      alert("Failed to fetch address details. Please try again.");
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressSearchRef.current && !addressSearchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) { setDomainDropdownOpen(false); setDomainSearchQuery(""); }
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(e.target)) { setSourceDropdownOpen(false); setSourceSearchQuery(""); }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) { setStatusDropdownOpen(false); setStatusSearchQuery(""); }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (addressSearchTimeoutRef.current) {
        clearTimeout(addressSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle country selection - find country_id from country_name
    if (name === "country") {
      const selectedCountry = countries.find(c => c.country_name === value);
      if (selectedCountry) {
        setSelectedCountryId(selectedCountry.country_id);
      } else {
        setSelectedCountryId(null);
        // Reset state and city when country is cleared
        setSelectedStateId(null);
        setStates([]);
        setCities([]);
        setFormData(prev => ({ ...prev, state: "", city: "" }));
      }
    }
    
    // Handle state selection - find state_id from state_name
    if (name === "state") {
      const selectedState = states.find(s => s.state_name === value);
      if (selectedState) {
        setSelectedStateId(selectedState.state_id);
      } else {
        setSelectedStateId(null);
        // Reset city when state is cleared
        setCities([]);
        setFormData(prev => ({ ...prev, city: "" }));
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleServiceChange = (subcategoryId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(subcategoryId)
        ? prev.services.filter(id => id !== subcategoryId)
        : [...prev.services, subcategoryId]
    }));
  };

  const handleAddServiceToCategory = (categoryId) => {
    setEditingService({
      id: `custom-${Date.now()}`,
      name: "",
      categoryId
    });
    setIsAddingServiceToCategory(categoryId);
  };

  const handleSaveService = () => {
    if (!editingService || !editingService.name.trim()) return;
    setCustomServices(prev => ({
      ...prev,
      [editingService.id]: { name: editingService.name.trim(), categoryId: editingService.categoryId }
    }));
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, editingService.id]
    }));
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const handleCancelEditService = () => {
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const handleDeleteCustomService = (id) => {
    if (!window.confirm("Remove this custom service?")) return;
    setCustomServices(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(s => s !== id)
    }));
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.personalPhone) {
      alert("Please fill in required fields (First Name and Phone).");
      return;
    }
    if (!formData.source) {
      alert("Please select a source.");
      return;
    }
    if (!formData.services || formData.services.length === 0) {
      alert("Please select at least one service.");
      return;
    }

    // Get logged-in user id from localStorage
    let createdBy = 1;
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        createdBy = user.user_id || user.id || 1;
      }
    } catch (error) {
      console.error("Error reading user data:", error);
    }

    // Find city_id, state_id, country_id from names
    const selectedCountry = countries.find(c => c.country_name === formData.country);
    const selectedState = states.find(s => s.state_name === formData.state);
    const selectedCity = cities.find(c => c.city_name === formData.city);

    // Build request payload matching backend API format
    const numericServiceIds = formData.services.filter(s => typeof s === "number");
    const customServiceNames = formData.services
      .filter(s => typeof s === "string")
      .map(id => customServices[id]?.name)
      .filter(Boolean);

    // Combine phone with country code if selected
    const phoneWithCode = phoneCountryCode
      ? `${phoneCountryCode}${formData.personalPhone}`
      : formData.personalPhone;

    // Map selected status id to status name for APIs that expect either/both
    const statusId = formData.status ? parseInt(formData.status, 10) : null;
    const statusObj = leadStatuses.find((s) => s.id === statusId);
    const statusName = statusObj ? statusObj.name : null;

    const payload = {
      First_Name: formData.firstName,
      Last_Name: formData.lastName,
      Email: formData.personalEmail || null,
      Phone: phoneWithCode,
      Alt_Phone: formData.altPhone || null,
      Source_Id: formData.source ? parseInt(formData.source, 10) : null,
      Status_Id: statusId,
      Status: statusName,
      Created_By: createdBy,
      Lead_Type: "Business", // Set lead type as Business
      services: numericServiceIds,
      ...(customServiceNames.length > 0 && { custom_services: customServiceNames }),
      Company_Name: formData.companyName || null,
      Company_Email: formData.companyEmail || null,
      Company_Phone: formData.companyPhone || null,
      Domain_Id: formData.domain ? parseInt(formData.domain, 10) : null,
      Website: formData.website || null,
      Address_Line_1: formData.addressLine1 || null,
      Address_Line_2: formData.addressLine2 || null,
      City_Id: selectedCity ? selectedCity.city_id : null,
      State_Id: selectedState ? selectedState.state_id : null,
      Country_Id: selectedCountry ? selectedCountry.country_id : null,
      Zip_Code: formData.zipCode || null,
      Com_Lang: formData.communicationLanguage || null,
      Is_Quote_Sent: formData.isQuoteSent ? 1 : 0
    };

    setSubmitting(true);
    try {
      const endpoint = '/create-leads';
      
      const response = await axiosInstance.post(endpoint, payload);
      
      const successMessage = response.data.message || "Lead created successfully";
      
      if (response.data && response.data.lead) {
        alert(successMessage);

        // Call parent callback with the created data
        if (onAddLead) {
          onAddLead(response.data.lead);
        }

        // Navigate back
        if (onBack) {
          onBack();
        }
      }
    } catch (error) {
      console.error("Error creating lead:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to create lead. Please try again.";
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="addlead-container">
      <button type="button" className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back
      </button>
      <div className="issues-main-card">
      <form className="addlead-form" onSubmit={handleSubmit}>
        {/* Personal and Company Information */}
        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>First Name <span className="required">*</span></label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Personal Email</label>
              <input
                type="email"
                name="personalEmail"
                value={formData.personalEmail}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Personal Phone <span className="required">*</span></label>
              <div style={{ display: "flex", gap: "8px" }}>
                <select
                  style={{ maxWidth: "120px" }}
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                >
                  <option value="">Code</option>
                  {countries.map((country) => {
                    const code = (country.country_code || country.country_short_code || "").trim();
                    return (
                      <option
                        key={country.country_id}
                        value={code}
                      >
                        {code}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="tel"
                  name="personalPhone"
                  value={formData.personalPhone}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Business</label>
              {domains.length > 0 ? (
                <div className="domain-filter-dropdown" ref={domainDropdownRef} style={{ minWidth: "100%" }}>
                  <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingDomains ? 0.6 : 1, cursor: loadingDomains ? "default" : "pointer" }} onClick={() => !loadingDomains && setDomainDropdownOpen((o) => !o)} role="button" tabIndex={loadingDomains ? -1 : 0} onKeyDown={(e) => { if (!loadingDomains && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setDomainDropdownOpen((o) => !o); } }}>
                    <span>{loadingDomains ? "Loading businesses..." : (formData.domain ? (domains.find((d) => String(d.id) === String(formData.domain))?.name || "Select business") : "Select business")}</span>
                    <FaChevronDown className={`domain-filter-chevron ${domainDropdownOpen ? "open" : ""}`} />
                  </div>
                  {domainDropdownOpen && !loadingDomains && (
                    <div className="domain-filter-panel">
                      <input type="text" className="domain-filter-search" placeholder="Search businesses..." value={domainSearchQuery} onChange={(e) => setDomainSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                      <div className="domain-filter-options">
                        <div className={`domain-filter-option ${!formData.domain ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: "" })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>Select business</div>
                        {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).map((d) => (
                          <div key={d.id} className={`domain-filter-option ${String(formData.domain) === String(d.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: String(d.id) })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>{d.name}</div>
                        ))}
                        {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No businesses found</div>}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                 
                />
              )}
            </div>
            <div className="form-group">
              <label>Company Email</label>
              <input
                type="email"
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Company Phone</label>
              <input
                type="tel"
                name="companyPhone"
                value={formData.companyPhone}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        {/* Communication, Source, and Status */}
        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>Communication Language</label>
              <input
                type="text"
                name="communicationLanguage"
                value={formData.communicationLanguage}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Source <span className="required">*</span></label>
              <div className="domain-filter-dropdown" ref={sourceDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingSources ? 0.6 : 1, cursor: loadingSources ? "default" : "pointer" }} onClick={() => !loadingSources && setSourceDropdownOpen((o) => !o)} role="button" tabIndex={loadingSources ? -1 : 0} onKeyDown={(e) => { if (!loadingSources && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setSourceDropdownOpen((o) => !o); } }}>
                  <span>{loadingSources ? "Loading sources..." : (formData.source ? (sources.find((s) => String(s.source_id ?? s.Source_Id ?? s.id) === String(formData.source))?.source_name || sources.find((s) => String(s.source_id ?? s.Source_Id ?? s.id) === String(formData.source))?.Source_Name || "Select Source") : "Select Source")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${sourceDropdownOpen ? "open" : ""}`} />
                </div>
                {sourceDropdownOpen && !loadingSources && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search sources..." value={sourceSearchQuery} onChange={(e) => setSourceSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.source ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, source: "" })); setSourceDropdownOpen(false); setSourceSearchQuery(""); }}>Select Source</div>
                      {sources.filter((s) => !sourceSearchQuery.trim() || String(s.source_name || s.Source_Name || "").toLowerCase().includes(sourceSearchQuery.trim().toLowerCase())).map((s) => {
                        const sid = String(s.source_id ?? s.Source_Id ?? s.id);
                        const sname = s.source_name || s.Source_Name || "";
                        return (
                          <div key={sid} className={`domain-filter-option ${String(formData.source) === sid ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, source: sid })); setSourceDropdownOpen(false); setSourceSearchQuery(""); }}>{sname}</div>
                        );
                      })}
                      {sources.filter((s) => !sourceSearchQuery.trim() || String(s.source_name || s.Source_Name || "").toLowerCase().includes(sourceSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No sources found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Status</label>
              <div className="domain-filter-dropdown" ref={statusDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingStatuses ? 0.6 : 1, cursor: loadingStatuses ? "default" : "pointer" }} onClick={() => !loadingStatuses && setStatusDropdownOpen((o) => !o)} role="button" tabIndex={loadingStatuses ? -1 : 0} onKeyDown={(e) => { if (!loadingStatuses && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setStatusDropdownOpen((o) => !o); } }}>
                  <span>{loadingStatuses ? "Loading statuses..." : (formData.status ? (leadStatuses.find((s) => String(s.id) === String(formData.status))?.name || "Select Status") : "Select Status")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${statusDropdownOpen ? "open" : ""}`} />
                </div>
                {statusDropdownOpen && !loadingStatuses && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search statuses..." value={statusSearchQuery} onChange={(e) => setStatusSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.status ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, status: "" })); setStatusDropdownOpen(false); setStatusSearchQuery(""); }}>Select Status</div>
                      {leadStatuses.filter((s) => !statusSearchQuery.trim() || String(s.name || "").toLowerCase().includes(statusSearchQuery.trim().toLowerCase())).map((s) => (
                        <div key={s.id} className={`domain-filter-option ${String(formData.status) === String(s.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, status: String(s.id) })); setStatusDropdownOpen(false); setStatusSearchQuery(""); }}>{s.name}</div>
                      ))}
                      {leadStatuses.filter((s) => !statusSearchQuery.trim() || String(s.name || "").toLowerCase().includes(statusSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No statuses found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quote Status */}
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isQuoteSent"
              checked={formData.isQuoteSent}
              onChange={handleInputChange}
            />
            <span>Is Quote Sent</span>
            <button
              type="button"
              className="btn-generate-quote"
              onClick={() => navigate("/generate-quote", { state: { customerData: formData, type: "lead" } })}
            >
              Generate Quote
            </button>
          </label>
        </div>

        {/* Services */}
        <div className="form-section addlead-section-no-line">
          <h2 className="section-title">Services <span className="required">*</span></h2>
          {loadingServices ? (
            <p>Loading services...</p>
          ) : (
            <div className="services-segregated-compact">
              <div className="services-compact-row">
                <label className="services-compact-label">Category</label>
                <select
                  className="services-category-select"
                  value={selectedServiceCategoryId ?? ""}
                  onChange={(e) => setSelectedServiceCategoryId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Select category</option>
                  {serviceCategories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>{cat.category_name}</option>
                  ))}
                </select>
              </div>
              <div className="services-subcategory-compact">
                {selectedServiceCategoryId ? (
                  (() => {
                    const category = serviceCategories.find(c => c.category_id === selectedServiceCategoryId);
                    const subs = category?.subcategories || [];
                    const customForCategory = Object.entries(customServices)
                      .filter(([, s]) => s.categoryId === selectedServiceCategoryId)
                      .map(([id, s]) => ({ id, name: s.name }));
                    return subs.length || customForCategory.length ? (
                      <>
                        <div className="services-subcategory-list-label">Services</div>
                        <div className="services-subcategory-grid">
                          {subs.map(sub => (
                            <label key={sub.subcategory_id} className="service-checkbox service-checkbox-compact">
                              <input
                                type="checkbox"
                                checked={formData.services.includes(sub.subcategory_id)}
                                onChange={() => handleServiceChange(sub.subcategory_id)}
                              />
                              <span>{sub.subcategory_name}</span>
                            </label>
                          ))}
                          {customForCategory.map(({ id, name }) => (
                            <div key={id} className="addlead-custom-service-row">
                              <label className="service-checkbox service-checkbox-compact">
                                <input
                                  type="checkbox"
                                  checked={formData.services.includes(id)}
                                  onChange={() => handleServiceChange(id)}
                                />
                                <span>{name}</span>
                              </label>
                              <button
                                type="button"
                                className="addlead-custom-service-delete"
                                onClick={() => handleDeleteCustomService(id)}
                                title="Remove"
                              >
                                <FaTrash size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="services-subcategory-empty">No sub-services in this category.</div>
                    );
                  })()
                ) : (
                  <div className="services-subcategory-empty">Select a category to see services.</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Address */}
        <div className="form-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "20px", paddingBottom: "10px", borderBottom: "1px solid #E5EDF5" }}>
            <h2 className="section-title" style={{ margin: 0, paddingBottom: 0, borderBottom: "none" }}>Address</h2>
            <div style={{ position: "relative", width: "300px", marginBottom: 0 }} ref={addressSearchRef}>
              <input
                type="text"
                className="filter-input"
                placeholder="Search Address..."
                value={addressSearch}
                onChange={handleAddressSearchChange}
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
              />
              {loadingAddressSuggestions && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1px solid #ddd", borderRadius: "4px", padding: "8px", zIndex: 1000 }}>
                  Searching...
                </div>
              )}
              {showSuggestions && addressSuggestions.length > 0 && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1px solid #ddd", borderRadius: "4px", maxHeight: "200px", overflowY: "auto", zIndex: 1000, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                  {addressSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleAddressSuggestionSelect(suggestion)}
                      style={{ padding: "10px", cursor: "pointer", borderBottom: index < addressSuggestions.length - 1 ? "1px solid #eee" : "none" }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "#f5f5f5"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "white"}
                    >
                      {suggestion.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Address Line 1</label>
              <input
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Country</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                disabled={loadingCountries}
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country.country_id} value={country.country_name}>
                    {country.country_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>State</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                disabled={!selectedCountryId || loadingStates}
              >
                <option value="">Select State</option>
                {states.map(state => (
                  <option key={state.state_id} value={state.state_name}>
                    {state.state_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>City</label>
              <select
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                disabled={!selectedStateId || loadingCities}
              >
                <option value="">Select City</option>
                {cities.map(city => (
                  <option key={city.city_id} value={city.city_name}>
                    {city.city_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <button type="submit" className="btn-add-lead" disabled={submitting} style={{ maxWidth: '200px' }}>
            {submitting ? "Adding..." : "Add Business Lead"}
          </button>
        </div>
      </form>
      </div>
      {editingService && (
        <div className="addlead-modal-overlay" onClick={handleCancelEditService}>
          <div className="addlead-modal addlead-add-service-modal" onClick={e => e.stopPropagation()}>
            <h3>
              {isAddingServiceToCategory
                ? "Add Service to " +
                  (serviceCategories.find((c) => c.category_id === isAddingServiceToCategory)?.category_name || "Category")
                : "Edit Service"}
            </h3>
            <div className="form-group">
              <label>Service Name</label>
              <input
                type="text"
                value={editingService.name}
                onChange={e => setEditingService({ ...editingService, name: e.target.value })}
              
              />
            </div>
            <div className="addlead-modal-actions">
              <button type="button" className="btn-cancel" onClick={handleCancelEditService}>Cancel</button>
              <button type="button" className="btn-add-lead" onClick={handleSaveService} disabled={!editingService.name?.trim()}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddBusinessLead;
